import { Deserializer } from 'jsonapi-serializer';
import { ACCOUNT_TYPES } from '../constants/account-constants';
import { COMMAND_STATE, EVENT_ACTION_KEYS } from '../constants/command-status';
import commands from '../constants/commands';
import { CREATED } from '../constants/response-status-codes';
import { AccountSubCmds, HidMobileAccessSubCmds, MaintenanceSubCmds, StidMobileSubCmds } from '../constants/sub-commands';
import { ExecuteCommand } from "../models/ExecuteCommand";
import { genericError } from '../models/GenericError';
import { APIConnector } from "./api/APIConnector";
import { getConstraintViolation, processServerError } from './utilities/ServiceError';
import { Translator } from '../contexts/translator';
import { TranslationKeys } from '../contexts/TranslationKeys';

const apiConnector = new APIConnector();

const TestData = {
    testData: [
        "1232SML0001202=192.1.2.94=4.6.0",
        "1232SML0001203=192.1.2.95=4.6.1",
        "1232SML0001204=192.1.2.96=4.6.1",
        "1232SML0001205=192.1.2.97=4.6.1",
        "1232SML0001206=192.1.2.98=4.6.1",
        "1232SML0001207=192.1.2.99=4.6.1",
        "1232SML0001208=192.1.2.100=4.6.1",
        "1232SML0001209=192.1.2.101=4.6.1",
        "1232SML0001210=192.1.2.102=4.6.1",
        "1232SML0001211=192.1.2.103=4.6.1",
        "1232SML0001212=192.1.2.104=4.6.1",
        "1232SML0001213=192.1.2.105=4.6.1",
        "1232SML0001214=192.1.2.106=4.6.1",
        "1232SML0001215=192.1.2.107=4.6.1",
        "1232SML0001216=192.1.2.108=4.6.1",
        "1232SML0001217=192.1.2.109=4.6.1",
        "1232SML0001218=192.1.2.110=4.6.1",
        "1232SML0001219=192.1.2.111=4.6.1",
        "1232SML0001220=192.1.2.112=4.6.1",
        "1232SML0001221=192.1.2.113=4.6.1",
        "1232SML0001222=192.1.2.114=4.6.1",
        "1232SML0001223=192.1.2.115=4.6.1",
        "1232SML0001224=192.1.2.116=4.6.1",
        "1232SML0001225=192.1.2.117=4.6.1",
    ]
};

const execute = async (command) => {
    const response = await apiConnector.sendData("executeCommand", true, command);

    return new Promise<any>(async (resolve, reject) => {
        const json = await response.json();

        if (json.errors) {
            return reject({ status: response.status, message: getConstraintViolation(json.errors[0].detail) })
        }

        await new Deserializer().deserialize(json, (error, data) => {
            if (error) {
                return reject({ status: response.status, id: 1, success: false, message: genericError })
            }

            const cmdStatus = data['cmd-status'];
            const cmdStatusMsg = data['cmd-status-msg'];

            if (cmdStatus === COMMAND_STATE.COMMAND_FAILED) {
                return reject({ status: cmdStatus, success: false, message: cmdStatusMsg });
            }

            const innerJson = data['json-response'];
            const json2 = (innerJson === '') ? '' : JSON.parse(innerJson);
            return resolve({ id: 1, success: true, status: data['cmd-status'], message: json2 })
        })

        return reject({ status: response.status, id: 1, success: false, message: genericError })
    })
}

export const executeCommandDeleteField = (executeCommand) => {
    try {
        const command = {
            data: {
                type: "executeCommand",
                id: 1,
                attributes: {
                    cmd: executeCommand.cmd,
                    cmdSubType: executeCommand.cmdSubType,
                    entityId: executeCommand.entityId,
                }
            }
        }

        return execute(command);
    } catch (error) {
        return new Promise((resolve, reject) => {
            reject(processServerError(error));
        });
    }
}

export const executeCommand = (executeCommand: ExecuteCommand, test = false) => {
    try {
        const testData = test ? TestData : undefined;

        const command = {
            data: {
                type: "executeCommand",
                id: 1,
                attributes: {
                    cmd: executeCommand.cmd,
                    cmdSubType: executeCommand.cmdSubType,
                    entityId: executeCommand.entityId,
                    userLoginId: executeCommand.userLoginId ?? undefined,
                    jsonResponse: executeCommand.jsonResponse ?? undefined,
                    controllerId: executeCommand.controllerId ?? undefined,
                    certificateId: executeCommand.certificateId ?? undefined,
                    startIp: executeCommand.startIp ?? undefined,
                    endIp: executeCommand.endIp ?? undefined,
                    fixedAddress: executeCommand.fixedAddress ?? undefined,
                    macAddress: executeCommand.macAddress ?? undefined,
                    syncType: executeCommand.syncType,
                    tlsUrl: executeCommand.tlsUrl ?? undefined,
                    clientId: executeCommand.clientId ?? undefined,
                    tlsPort: executeCommand.tlsPort ?? undefined,
                    searchTerm: executeCommand.searchTerm ?? undefined,
                    password: executeCommand.password ?? undefined,
                    confirmPassword: executeCommand.confirmPassword ?? undefined, // TODO: look to remove this field from the command.
                    ...(executeCommand.addresses && {
                        addresses: executeCommand.addresses
                    }),
                    ...testData
                }
            }
        }

        return execute(command);
    } catch (error) {
        return new Promise((resolve, reject) => {
            reject(processServerError(error));
        });
    }
}

export const executeCommandTest = (executeCommand: ExecuteCommand, testData?: any) => {
    try {
        const command = {
            data: {
                type: "executeCommand",
                id: 1,
                attributes: {
                    cmd: executeCommand.cmd,
                    cmdSubType: executeCommand.cmdSubType,
                    userLoginId: executeCommand.userLoginId ?? undefined,
                    jsonResponse: executeCommand.jsonResponse ?? undefined,
                    startIp: executeCommand.startIp ?? undefined,
                    endIp: executeCommand.endIp ?? undefined,
                    ...testData
                }
            }
        }

        return execute(command);
    } catch (error) {
        return new Promise((resolve, reject) => {
            reject(processServerError(error));
        });
    }
}

export const executeCommandForDevice = (executeCommand: ExecuteCommand) => {
    try {
        const command = {
            data: {
                type: "executeCommand",
                id: 1,
                attributes: {
                    cmd: executeCommand.cmd,
                    cmdSubType: executeCommand.cmdSubType,
                    userLoginId: executeCommand.userLoginId,
                    jsonResponse: executeCommand.jsonResponse,
                    fixedAddress: executeCommand.fixedAddress
                }
            }
        }

        return execute(command)
    } catch (error) {
        return new Promise((resolve, reject) => {
            reject(processServerError(error));
        });
    }
}

export const enrolVingCard = async (vingcard: any, translate: Translator) => {
    try {
        const command = {
            data: {
                type: "executeCommand",
                id: 1,
                attributes: {
                    cmd: commands.Account.label,
                    cmdSubType: AccountSubCmds.Encode_Vingcard_User,
                    masterId: vingcard.masterId,
                    userGroup: vingcard.userGroup,
                    expireTime: vingcard.expireTime,
                    doors: vingcard.doors,
                    timeSchedule: vingcard.timeSchedule,
                    checkoutTime: vingcard.checkoutTime,
                    checkinTime: vingcard.checkinTime,
                    roomNumber: vingcard.roomNumber
                }
            }
        }

        let response: Response;
        await apiConnector.sendData("executeCommand", true, command).then(data => {
            response = data;
        }).catch(error => {
            return new Promise((resolve, reject) => {
                reject({ status: response.status, message: translate.byKey(TranslationKeys.error) })
            });
        });

        return new Promise<any>(async (resolve, reject) => {
            const json = await response.json();

            if (json.errors) {
                return reject({ status: response.status, message: getConstraintViolation(json.errors[0].detail) })
            }

            new Deserializer().deserialize(json, (err: any, data: any) => {
                if (data['cmd-status'] === COMMAND_STATE.COMMAND_COMPLETE) {
                    return resolve({ id: 1, success: true })
                }

                return reject({ status: response.status, id: 1, success: false, message: data["cmd-status-msg"] })
            });
        })

    } catch (error) {
        return Promise.reject(processServerError(error));
    }
}


export const generateMobileAccessId = async (accountType: number, masterId: string, masterEmail: string, enrolFunctionId: string) => {
    try {
        const command = {
            data: {
                type: "executeCommand",
                id: 1,
                attributes: {
                    cmd: commands.Account.label,
                    cmdSubType: (accountType === ACCOUNT_TYPES.ID_HID_MOBILE_ACCESS) ? HidMobileAccessSubCmds.Generate_Credential : StidMobileSubCmds.Generate_Credential,
                    masterId: masterId,
                    emailAddress: masterEmail,
                    enrolFunctionId: enrolFunctionId,
                }
            }
        }

        let response: Response;

        await apiConnector.sendData("executeCommand", true, command).then(data => {
            response = data;
        }).catch(error => {
            return new Promise((resolve, reject) => {
                reject({ status: response.status, message: genericError })
            });
        });

        return new Promise<any>(async (resolve, reject) => {
            const json = await response.json();

            if (json.errors) {
                return reject({ status: response.status, message: getConstraintViolation(json.errors[0].detail) })
            }

            new Deserializer().deserialize(json, (err: any, data: any) => {
                if (response.status === CREATED) {
                    const responseData = {
                        mobileAccessIdentifier: data['list'][0].split('=')[1],
                        tagCode: data['list'][1].split('=')[1]
                    };
                    return resolve({ id: 1, success: true, data: responseData })
                }

                return reject({ status: response.status, id: 1, success: false, message: genericError })
            });
        })
    } catch (error) {
        return new Promise((resolve, reject) => {
            reject(processServerError(error));
        });
    }
}

export const getAllAlloweOrDeniedActionsId = async (deviceId: string, eventGroup: string) => {
    try {
        const command = {
            data: {
                type: "executeCommand",
                id: 1,
                attributes: {
                    cmd: commands.Device.label,
                    cmdSubType: eventGroup === EVENT_ACTION_KEYS.ALL_ALLOWED_KEY ? MaintenanceSubCmds.All_Allowed_Action : MaintenanceSubCmds.All_Denied_Action,
                    deviceId: deviceId
                }
            }
        }

        let response: Response;

        await apiConnector.sendData("executeCommand", true, command).then(data => {
            response = data;
        }).catch(error => {
            return new Promise((resolve, reject) => {
                reject(processServerError(error));
            })
        });

        return new Promise<any>(async (resolve, reject) => {
            const json = await response.json();

            if (json.errors) {
                return reject({ status: response.status, message: getConstraintViolation(json.errors[0].detail) })
            }

            new Deserializer().deserialize(json, (err: any, data: any) => {
                if (response.status === CREATED) {
                    return resolve({ id: 1, success: true, data: { deviceId: data['device-id'], actions: data['list'] } })
                }

                return reject({ status: response.status, id: 1, success: false, message: genericError });
            });
        })
    } catch (error) {
        return new Promise((resolve, reject) => {
            reject(processServerError(error));
        });
    }
}

export const executeCommandForBiometricDevices = (executeCommand: ExecuteCommand) => {
    try {
        const command = {
            data: {
                type: "executeCommand",
                id: 1,
                attributes: {
                    cmd: executeCommand.cmd,
                    cmdSubType: executeCommand.cmdSubType,
                    ...(executeCommand.state && {
                        state: executeCommand.state
                    }),
                    ...(executeCommand.addresses && {
                        addresses: executeCommand.addresses
                    }),
                    ...(executeCommand.ids && {
                        ids: executeCommand.ids
                    })
                }
            }
        }

        return execute(command)
    } catch (error) {
        return new Promise((resolve, reject) => {
            reject(processServerError(error));
        });
    }
}

export const executeCommandForSavingTemplate = (executeCommand: ExecuteCommand) => {
    try {
        const command = {
            data: {
                type: "executeCommand",
                id: 1,
                attributes: {
                    cmd: executeCommand.cmd,
                    cmdSubType: executeCommand.cmdSubType,
                    tagId: executeCommand.tagId,
                    finger: executeCommand.finger,
                    jsonResponse: executeCommand.jsonResponse
                }
            }
        }
        return execute(command);
    } catch (error) {
        return new Promise((resolve, reject) => {
            reject(processServerError(error));
        });
    }
}

export const requestEnrolDevice = async (executeCommand: ExecuteCommand) => {
    try {
        const command = {
            data: {
                type: "executeCommand",
                id: 1,
                attributes: {
                    cmd: executeCommand.cmd,
                    cmdSubType: executeCommand.cmdSubType,
                }
            }
        }
        let response: Response;

        await apiConnector.sendData("executeCommand", true, command).then(data => {
            response = data;
        }).catch(error => {
            return new Promise((resolve, reject) => {
                reject({ status: response.status, message: genericError });
            })
        });

        return new Promise<any>(async (resolve, reject) => {
            const json = await response.json();
            if (json.errors) {
                return reject({ status: response.status, message: getConstraintViolation(json.errors[0].detail) })
            }

            new Deserializer().deserialize(json, (err: any, data: any) => {
                if (response.status === CREATED && data['cmd-status'] === COMMAND_STATE.COMMAND_COMPLETE) {
                    return resolve({ id: 1, success: true, ids: data['ids'] })
                }

                return reject({ status: response.status, id: 1, success: false, message: genericError })
            })
        })
    } catch (error) {
        return new Promise((resolve, reject) => {
            reject(processServerError(error));
        });
    }
}

export const enrolHand = async (executeCommand: ExecuteCommand) => {
    try {
        const command = {
            data: {
                type: "executeCommand",
                id: 1,
                attributes: {
                    cmd: executeCommand.cmd,
                    cmdSubType: executeCommand.cmdSubType,
                    deviceId: executeCommand.deviceId,
                    tagId: executeCommand.tagId,
                    finger: 0,
                    captureType: executeCommand.captureType
                }
            }
        };

        let response: Response;

        const timeoutPromise = new Promise<never>((_, reject) => {
            setTimeout(() => {
                reject({
                    status: 408,
                    id: 1,
                    success: false,
                    message: "Operation timed out after 1 minute"
                });
            }, 60000);
        });

        const apiPromise = apiConnector.sendData("executeCommand", true, command)
            .then(data => {
                response = data;
                return response;
            })
            .catch(error => {
                throw processServerError(error);
            });

        response = await Promise.race([apiPromise, timeoutPromise]);

        return new Promise<any>(async (resolve, reject) => {
            const json = await response.json();
            if (json.errors) {
                return reject({
                    status: response.status,
                    message: getConstraintViolation(json.errors[0].detail)
                });
            }

            new Deserializer().deserialize(json, (err: any, data: any) => {
                if (response.status === CREATED) {
                    return resolve({ id: 1, success: true, data: data })
                }

                return reject({
                    status: response.status,
                    id: 1,
                    success: false,
                    message: genericError
                });
            });
        });
    } catch (error) {
        return Promise.reject(processServerError(error));
    }
};

export const enrolFinger = async (executeCommand: ExecuteCommand) => {
    try {
        const command = {
            data: {
                type: "executeCommand",
                id: 1,
                attributes: {
                    cmd: executeCommand.cmd,
                    cmdSubType: executeCommand.cmdSubType,
                    deviceId: executeCommand.deviceId,
                    tagId: executeCommand.tagId,
                    finger: 0,
                    captureType: executeCommand.captureType
                }
            }
        }

        let response: Response;
        await apiConnector.sendData("executeCommand", true, command).then(data => {
            response = data;
        }).catch(error => {
            return new Promise((resolve, reject) => {
                reject(processServerError(error));
            });
        });

        return new Promise<any>(async (resolve, reject) => {
            const json = await response.json();
            if (json.errors) {
                return reject({ status: response.status, message: getConstraintViolation(json.errors[0].detail) })
            }

            new Deserializer().deserialize(json, (err: any, data: any) => {
                if (response.status === CREATED) {
                    return resolve({ id: 1, success: true, data: data })
                }

                return reject({ status: response.status, id: 1, success: false, message: genericError })
            })
        })
    } catch (error) {
        return new Promise((resolve, reject) => {
            reject(processServerError(error));
        });
    }
}

export const enrolFace = async (executeCommand: ExecuteCommand) => {
    try {
        const command = {
            data: {
                type: "executeCommand",
                id: 1,
                attributes: {
                    cmd: executeCommand.cmd,
                    cmdSubType: executeCommand.cmdSubType,
                    deviceId: executeCommand.deviceId,
                    image: executeCommand.image,
                    tagId: executeCommand.tagId,
                    captureType: 5
                }
            }
        }

        let response: Response;
        await apiConnector.sendData("executeCommand", true, command).then(data => {
            response = data;
        }).catch(error => {
            return new Promise((resolve, reject) => {
                reject({ status: response.status, message: genericError })
            })
        });

        return new Promise<any>(async (resolve, reject) => {
            const json = await response.json();

            if (json.errors) {
                return reject({ status: response.status, message: getConstraintViolation(json.errors[0].detail) })
            }

            new Deserializer().deserialize(json, (err: any, data: any) => {
                if (response.status === CREATED) {
                    return resolve({ id: 1, success: true, data: data })
                }

                return reject({ status: response.status, id: 1, success: false, message: genericError });
            })
        })
    } catch (error) {
        return new Promise((resolve, reject) => {
            reject(processServerError(error));
        });
    }
}

export const executeCommandForFakeBiometricDevicesStatuses = () => {
    try {
        const command = {
            data: {

                type: "executeCommand",
                id: 1,
                attributes: {
                    cmd: "DEVICE",
                    cmdSubType: "TEST_DEVICE_STATUS",
                    testData: ["1421SMS000281=2=OFFLINE", "1421SMS0000012=0=ONLINE"]
                }
            }
        }

        return execute(command)
    } catch (error) {
        return new Promise((resolve, reject) => {
            reject(processServerError(error));
        });
    }
}

export const executeCommandFakeUserCheck = () => {
    try {
        const command = {
            data: {
                type: "executeCommand",
                id: 1,
                attributes: {
                    cmd: "DEVICE",
                    cmdSubType: "USER_ID_LIST",
                    testData: "test"
                }
            }
        }
        return execute(command);
    } catch (error) {
        return new Promise((resolve, reject) => {
            reject(processServerError(error));
        });
    }
}

export const replaceController = async (fixedAddressOld: string, fixedAddress: string) => {
    try {
        const command = {
            data: {
                type: 'executeCommand',
                id: 1,
                attributes: {
                    cmd: "ENGINE",
                    cmdSubType: "REPLACE_CONTROLLER",
                    fixedAddressOld: fixedAddressOld,
                    fixedAddress: fixedAddress
                }
            }
        }

        return execute(command);
    } catch (error) {
        return new Promise((resolve, reject) => {
            reject(processServerError(error));
        });
    }
}


export const executeCommandAreaTree = async (executeCommand: ExecuteCommand) => {
    try {
        const command = {
            data: {
                type: "executeCommand",
                id: 1,
                attributes: executeCommand
            }
        }

        let response: Response;
        await apiConnector.sendData("executeCommand", true, command).then(data => {
            response = data;
        }).catch(error => {
            return new Promise((resolve, reject) => {
                reject(processServerError(error));
            });
        });

        return new Promise<any>(async (resolve, reject) => {
            const json = await response.json();
            if (json.errors) {
                return reject({ status: response.status, message: getConstraintViolation(json.errors[0].detail) })
            }

            new Deserializer().deserialize(json, (err: any, data: any) => {
                if (response.status === CREATED) {
                    return resolve({ id: 1, success: true, data: data })
                }

                return reject({ status: response.status, id: 1, success: false, message: genericError })
            })
        })
    } catch (error) {
        return new Promise((resolve, reject) => {
            reject(processServerError(error));
        });
    }
}


export const executeCommandWidgetCommands = async (executeCommand: ExecuteCommand) => {
    try {
        const command = {
            data: {
                type: "executeCommand",
                id: 1,
                attributes: executeCommand
            }
        }

        return execute(command);
    } catch (error) {
        return new Promise((resolve, reject) => {
            reject(processServerError(error));
        });
    }
}



export const executeLockerCommands = async (executeCommand: ExecuteCommand) => {
    try {
        const command = {
            data: {
                type: "executeCommand",
                id: 1,
                attributes: executeCommand
            }
        }

        return execute(command);
    } catch (error) {
        return new Promise((resolve, reject) => {
            reject(processServerError(error));
        });
    }
}
